import { ReactNode, useState } from 'react';
import { Alert, CircularProgress } from '@mui/material';
import { deleteAlert } from 'api/alerts';
import { Alert as AlertData } from 'api/alerts/alerts.types';
import { ResStatus } from 'api/api.types';
import {
  getAlertsCountState,
  getAlertsInboxState,
  useAlerts,
} from 'contexts/alertsContext/alertsContext';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  margin: 1.5rem 0 0.5rem;
`;

const CounterpartyLink = styled(Link)`
  color: #0277bd;
  text-decoration: underline;

  &:hover {
    color: #01579b;
  }
`;

const ShowMoreLink = styled.span`
  display: block;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 500;
  color: #0286db;
  cursor: pointer;
  margin-top: 6px;

  &:hover {
    color: #005177;
  }
`;

const INITIAL_VISIBLE = 3;

const normalizeCounterpartyLinks = (message: string) =>
  message.replace(
    /&lt;a\s+href=&quot;(#\/files\/counterparties\/\d+)&quot;&gt;([\s\S]*?)&lt;\/a&gt;/gi,
    '<a href="$1">$2</a>',
  );

const renderText = (text: string) =>
  parse(text.replace(/</g, '&lt;').replace(/>/g, '&gt;'));

const renderMessage = (message: string) => {
  const normalizedMessage = normalizeCounterpartyLinks(message);
  const counterpartyLinks =
    /<a\s+href=["']#(\/files\/counterparties\/\d+)["']\s*>([\s\S]*?)<\/a>/gi;
  const parts: ReactNode[] = [];
  let textStart = 0;
  let match: RegExpExecArray | null;

  while ((match = counterpartyLinks.exec(normalizedMessage)) !== null) {
    const index = match.index;
    parts.push(renderText(normalizedMessage.slice(textStart, index)));
    parts.push(
      <CounterpartyLink key={index} to={match[1]}>
        {renderText(match[2])}
      </CounterpartyLink>,
    );
    textStart = index + match[0].length;
  }

  parts.push(renderText(normalizedMessage.slice(textStart)));
  return parts;
};

interface Props {
  alerts: AlertData[];
  onDismiss: (id: number) => void;
}

export const SaldeoErrorsList = ({ alerts, onDismiss }: Props) => {
  //TODO: Rename in the future to something like ErrorsList, since we use it now for Saldeo and Enova
  const { dispatch } = useAlerts();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslation(['misc']);

  if (!alerts.length) return null;

  const visibleAlerts = showAll ? alerts : alerts.slice(0, INITIAL_VISIBLE);
  const hiddenCount = alerts.length - INITIAL_VISIBLE;

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    const res = await deleteAlert(id);
    if (res.data.status === ResStatus.OK) {
      await getAlertsInboxState(dispatch);
      await getAlertsCountState(dispatch);
      onDismiss(id);
    }
    setDeletingId(null);
  };

  return (
    <Container>
      {visibleAlerts.map((alert) => {
        const isDeleting = deletingId === alert.id;
        return (
          <Alert
            key={alert.id}
            severity={alert.displayType === 'ERROR' ? 'error' : 'warning'}
            sx={{
              margin: '4px 0',
              opacity: isDeleting ? 0.6 : 1,
              transition: 'opacity 0.2s',
              '& .MuiAlert-message': {
                whiteSpace: 'pre-line',
              },
            }}
            action={
              isDeleting ? (
                <CircularProgress
                  size={18}
                  color='inherit'
                  sx={{ mt: '2px' }}
                />
              ) : undefined
            }
            onClose={isDeleting ? undefined : () => handleDelete(alert.id)}
          >
            {renderMessage(alert.message)}
          </Alert>
        );
      })}
      {!showAll && hiddenCount > 0 && (
        <ShowMoreLink onClick={() => setShowAll(true)}>
          {t('button.showMore', { ns: 'misc', count: hiddenCount })}
        </ShowMoreLink>
      )}
    </Container>
  );
};
